import { useNavigate } from 'react-router-dom';
import { FileText, FolderOpen, History, TrendingUp, Link2, Copy, Check, Download, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireBusiness } from '@/components/RequireBusiness';
import { useApp } from '@/contexts/AppContext';
import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

export default function DashboardPage() {
  return (
    <RequireBusiness>
      <DashboardContent />
    </RequireBusiness>
  );
}

function DashboardContent() {
  const navigate = useNavigate();
  const { stats, business } = useApp();
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  // Always use production domain for customer-facing review links
  const productionDomain = 'https://reviewplum.com';
  const customerReviewUrl = business 
    ? `${productionDomain}/review/${business.id}`
    : null;

  const handleCopyLink = () => {
    if (customerReviewUrl) {
      navigator.clipboard.writeText(customerReviewUrl);
      setCopied(true);
      toast.success('Customer review link copied!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleDownloadQR = useCallback(() => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    canvas.width = 300;
    canvas.height = 300;
    
    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, 300, 300);
        
        const link = document.createElement('a');
        link.download = `${business?.name || 'review'}-qr-code.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('QR code downloaded!');
      }
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [business?.name]);

  const handleShareQR = useCallback(async () => {
    if (!customerReviewUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Review ${business?.name}`,
          text: `Share your experience with ${business?.name}!`,
          url: customerReviewUrl,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  }, [customerReviewUrl, business?.name]);

  const statCards = [
    { 
      label: 'Reviews Generated', 
      value: stats.reviewsGenerated, 
      icon: FileText,
    },
    { 
      label: 'Reviews Submitted', 
      value: stats.reviewsSubmitted, 
      icon: TrendingUp,
    },
    { 
      label: 'Categories', 
      value: stats.categories, 
      icon: FolderOpen,
    },
  ];

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto w-full">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="section-title">Dashboard</h1>
          {business && (
            <p className="section-subtitle mt-1">{business.name}</p>
          )}
        </div>

        {/* Customer Review Link Card with QR Code */}
        {customerReviewUrl && (
          <div className="soft-card bg-gradient-to-br from-accent/50 to-accent mb-6 animate-fade-in overflow-hidden">
            <div className="flex flex-col gap-4">
              {/* QR Code Section - Centered on Mobile */}
              <div className="flex flex-col items-center gap-3">
                <div 
                  ref={qrRef}
                  className="p-3 bg-white rounded-xl shadow-sm"
                >
                  <QRCodeSVG
                    value={customerReviewUrl}
                    size={100}
                    level="H"
                    includeMargin={false}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleDownloadQR}
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                  >
                    <Download size={14} className="mr-1" />
                    Download
                  </Button>
                  <Button
                    onClick={handleShareQR}
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                  >
                    <Share2 size={14} className="mr-1" />
                    Share
                  </Button>
                </div>
              </div>

              {/* Link Section */}
              <div className="min-w-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="stat-icon shrink-0 w-10 h-10">
                    <Link2 size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm">Customer Review Link</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Share this link or QR code with customers
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <code className="text-xs bg-background/80 px-3 py-2 rounded-lg border border-border block overflow-hidden text-ellipsis whitespace-nowrap">
                    {customerReviewUrl}
                  </code>
                  <Button
                    onClick={handleCopyLink}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    {copied ? (
                      <>
                        <Check className="mr-1.5" size={14} />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="mr-1.5" size={14} />
                        Copy Link
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
          {statCards.map(({ label, value, icon: Icon }, index) => (
            <div 
              key={label} 
              className="soft-card flex flex-col items-center gap-2 p-3 sm:p-5 animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="stat-icon w-9 h-9 sm:w-12 sm:h-12">
                <Icon size={16} className="sm:w-[22px] sm:h-[22px]" />
              </div>
              <div className="text-center">
                <p className="text-lg sm:text-3xl font-semibold">{value}</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground leading-tight">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Primary CTA */}
        <div className="soft-card mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold">Ready to generate reviews?</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Create Google-compliant review suggestions
              </p>
            </div>
            <Button 
              onClick={() => navigate('/generate')}
              size="lg"
              className="w-full sm:w-auto h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <FileText className="mr-2" size={18} />
              Generate Review
            </Button>
          </div>
        </div>

        {/* Secondary CTAs */}
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          <button
            onClick={() => navigate('/history')}
            className="soft-card hover:border-primary/30 transition-all text-left group p-3 sm:p-5"
          >
            <div className="flex flex-col items-center gap-2 sm:gap-3">
              <div className="stat-icon group-hover:bg-primary/10 transition-colors w-9 h-9 sm:w-12 sm:h-12">
                <History size={16} className="sm:w-5 sm:h-5" />
              </div>
              <div className="text-center">
                <p className="font-medium text-xs sm:text-base">History</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground hidden sm:block">See all reviews</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/categories')}
            className="soft-card hover:border-primary/30 transition-all text-left group p-3 sm:p-5"
          >
            <div className="flex flex-col items-center gap-2 sm:gap-3">
              <div className="stat-icon group-hover:bg-primary/10 transition-colors w-9 h-9 sm:w-12 sm:h-12">
                <FolderOpen size={16} className="sm:w-5 sm:h-5" />
              </div>
              <div className="text-center">
                <p className="font-medium text-xs sm:text-base">Categories</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground hidden sm:block">Organize types</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
